import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  Divider,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  InfoOutlined as InfoIcon,
  LocationOnOutlined as LocationIcon,
  ArrowBack as ArrowBackIcon,
  RoomPreferencesOutlined as RoomPreferencesOutlinedIcon,
  PhoneOutlined as PhoneIcon,
  EmailOutlined as EmailIcon,
  LanguageOutlined as WebsiteIcon,
} from '@mui/icons-material';
import { customerService } from '../../services/api';

const BusinessForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [originalData, setOriginalData] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [formData, setFormData] = useState({
    display_name: '',
    logo: '',
    persona_full_name: '',
    persona_legal_name: '',
    persona_tax_id: '',
    persona_registration_number: '',
    persona_email: '',
    persona_website: '',
    persona_country: 'BR',
    persona_is_headquarters: false,
    address_street: '',
    address_number: '',
    address_complement: '',
    address_district: '',
    address_city: '',
    address_state: '',
    address_postal_code: '',
    address_country: 'BR',
    phone_number: '',
  });

  useEffect(() => {
    if (isEditMode) {
      fetchBusiness();
    }
  }, [id]);

  const fetchBusiness = async () => {
    try {
      setLoading(true);
      const response = await customerService.getBusiness(id);
      const business = response.data;

      // Extract data from nested structure
      const primaryAddress = business.addresses?.[0] || {};
      const primaryPhone = business.phones?.[0] || {};

      const newFormData = {
        display_name: business.display_name || '',
        logo: business.logo || '',
        persona_full_name: business.persona?.full_name || '',
        persona_legal_name: business.persona?.legal_name || '',
        persona_tax_id: business.persona?.tax_id || '',
        persona_registration_number: business.persona?.registration_number || '',
        persona_email: business.persona?.email || '',
        persona_website: business.persona?.website || '',
        persona_country: business.persona?.country || 'BR',
        persona_is_headquarters: business.persona?.is_headquarters || false,
        address_street: primaryAddress.street || '',
        address_number: primaryAddress.number || '',
        address_complement: primaryAddress.complement || '',
        address_district: primaryAddress.district || '',
        address_city: primaryAddress.city || '',
        address_state: primaryAddress.state || '',
        address_postal_code: primaryAddress.postal_code || '',
        address_country: primaryAddress.country || 'BR',
        phone_number: primaryPhone.number || '',
      };

      setFormData(newFormData);
      setOriginalData(newFormData);
    } catch (err) {
      console.error('Error fetching business:', err);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Email validation
    if (formData.persona_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.persona_email)) {
      newErrors.persona_email = t('businessManagement.invalidEmail');
    }

    // Website validation
    if (formData.persona_website && !/^(https?:\/\/)?(www\.)?[a-zA-Z0-9-]+(\.[a-zA-Z]{2,})+/.test(formData.persona_website)) {
      newErrors.persona_website = t('businessManagement.invalidWebsite');
    }

    // Required fields
    if (!formData.display_name?.trim()) {
      newErrors.display_name = t('businessManagement.required');
    }

    if (!formData.persona_full_name?.trim()) {
      newErrors.persona_full_name = t('businessManagement.required');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFormChange = (field, value) => {
    const newFormData = {
      ...formData,
      [field]: value,
    };
    setFormData(newFormData);

    // Check if there are changes
    if (isEditMode && originalData) {
      const changed = Object.keys(newFormData).some(
        key => newFormData[key] !== originalData[key]
      );
      setHasChanges(changed);
    } else {
      setHasChanges(true);
    }

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const handleSave = async () => {
    if (!validateForm()) {
      setSnackbar({
        open: true,
        message: t('businessManagement.validationError'),
        severity: 'error',
      });
      return;
    }

    try {
      setLoading(true);
      if (isEditMode) {
        await customerService.updateBusiness(id, formData);
        setSnackbar({
          open: true,
          message: t('businessManagement.updateSuccess'),
          severity: 'success',
        });
      } else {
        await customerService.createBusiness(formData);
        setSnackbar({
          open: true,
          message: t('businessManagement.createSuccess'),
          severity: 'success',
        });
      }
      setTimeout(() => navigate('/business-management'), 1500);
    } catch (error) {
      console.error('Error saving business:', error);
      setSnackbar({
        open: true,
        message: t('businessManagement.saveError'),
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleCancel = () => {
    navigate('/business-management');
  };

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          <Typography>{t('common.loading')}...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#f5f5f5', minHeight: '100vh', pb: 4 }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ pt: 4, pb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleCancel}
            sx={{ mb: 2, textTransform: 'none' }}
          >
            {t('businessManagement.backToList')}
          </Button>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <RoomPreferencesOutlinedIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            <Typography variant="h5" component="h1" fontWeight={600}>
              {isEditMode ? t('businessManagement.editTitle') : t('businessManagement.addTitle')}
            </Typography>
          </Box>
        </Box>

        {/* Card Informações do Business */}
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <InfoIcon color="primary" sx={{ fontSize: 24 }} />
              <Typography variant="h6" fontWeight={600}>
                {t('businessManagement.businessInfo')}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('businessManagement.businessInfoDescription')}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <Box sx={{ flex: '1 1 300px', minWidth: { xs: '100%', sm: 'calc(50% - 8px)' } }}>
                  <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                    {t('businessManagement.displayName')} *
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder={t('businessManagement.displayName')}
                    value={formData.display_name}
                    onChange={(e) => handleFormChange('display_name', e.target.value)}
                    error={!!errors.display_name}
                    helperText={errors.display_name}
                  />
                </Box>
                <Box sx={{ flex: '1 1 300px', minWidth: { xs: '100%', sm: 'calc(50% - 8px)' } }}>
                  <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                    {t('businessManagement.logoUrl')}
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder={t('businessManagement.logoUrl')}
                    value={formData.logo}
                    onChange={(e) => handleFormChange('logo', e.target.value)}
                  />
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <Box sx={{ flex: '1 1 300px', minWidth: { xs: '100%', sm: 'calc(50% - 8px)' } }}>
                  <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                    {t('businessManagement.fullName')} *
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder={t('businessManagement.fullName')}
                    value={formData.persona_full_name}
                    onChange={(e) => handleFormChange('persona_full_name', e.target.value)}
                    error={!!errors.persona_full_name}
                    helperText={errors.persona_full_name}
                  />
                </Box>
                <Box sx={{ flex: '1 1 300px', minWidth: { xs: '100%', sm: 'calc(50% - 8px)' } }}>
                  <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                    {t('businessManagement.legalName')}
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder={t('businessManagement.legalName')}
                    value={formData.persona_legal_name}
                    onChange={(e) => handleFormChange('persona_legal_name', e.target.value)}
                  />
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <Box sx={{ flex: '1 1 200px', minWidth: { xs: '100%', sm: 'calc(33.333% - 11px)' } }}>
                  <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                    {t('businessManagement.taxId')}
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder={t('businessManagement.taxId')}
                    value={formData.persona_tax_id}
                    onChange={(e) => handleFormChange('persona_tax_id', e.target.value)}
                  />
                </Box>
                <Box sx={{ flex: '1 1 200px', minWidth: { xs: '100%', sm: 'calc(33.333% - 11px)' } }}>
                  <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                    {t('businessManagement.registrationNumber')}
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder={t('businessManagement.registrationNumber')}
                    value={formData.persona_registration_number}
                    onChange={(e) => handleFormChange('persona_registration_number', e.target.value)}
                  />
                </Box>
                <Box sx={{ flex: '1 1 200px', minWidth: { xs: '100%', sm: 'calc(33.333% - 11px)' } }}>
                  <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                    {t('businessManagement.phone')}
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder={t('businessManagement.phone')}
                    value={formData.phone_number}
                    onChange={(e) => handleFormChange('phone_number', e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <Box sx={{ flex: '1 1 300px', minWidth: { xs: '100%', sm: 'calc(50% - 8px)' } }}>
                  <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <EmailIcon fontSize="small" />
                    {t('businessManagement.email')}
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    type="email"
                    placeholder={t('businessManagement.email')}
                    value={formData.persona_email}
                    onChange={(e) => handleFormChange('persona_email', e.target.value)}
                    error={!!errors.persona_email}
                    helperText={errors.persona_email}
                  />
                </Box>
                <Box sx={{ flex: '1 1 300px', minWidth: { xs: '100%', sm: 'calc(50% - 8px)' } }}>
                  <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <WebsiteIcon fontSize="small" />
                    {t('businessManagement.website')}
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder={t('businessManagement.website')}
                    value={formData.persona_website}
                    onChange={(e) => handleFormChange('persona_website', e.target.value)}
                    error={!!errors.persona_website}
                    helperText={errors.persona_website}
                  />
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <LocationIcon color="primary" sx={{ fontSize: 24 }} />
              <Typography variant="h6" fontWeight={600}>
                {t('businessManagement.address')}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('businessManagement.addressDescription')}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <Box sx={{ flex: '2 1 400px', minWidth: { xs: '100%', sm: 'calc(66.666% - 8px)' } }}>
                  <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                    {t('businessManagement.street')}
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder={t('businessManagement.street')}
                    value={formData.address_street}
                    onChange={(e) => handleFormChange('address_street', e.target.value)}
                  />
                </Box>
                <Box sx={{ flex: '1 1 200px', minWidth: { xs: '100%', sm: 'calc(33.333% - 8px)' } }}>
                  <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                    {t('businessManagement.number')}
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder={t('businessManagement.number')}
                    value={formData.address_number}
                    onChange={(e) => handleFormChange('address_number', e.target.value)}
                  />
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <Box sx={{ flex: '1 1 300px', minWidth: { xs: '100%', sm: 'calc(50% - 8px)' } }}>
                  <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                    {t('businessManagement.complement')}
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder={t('businessManagement.complement')}
                    value={formData.address_complement}
                    onChange={(e) => handleFormChange('address_complement', e.target.value)}
                  />
                </Box>
                <Box sx={{ flex: '1 1 300px', minWidth: { xs: '100%', sm: 'calc(50% - 8px)' } }}>
                  <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                    {t('businessManagement.district')}
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder={t('businessManagement.district')}
                    value={formData.address_district}
                    onChange={(e) => handleFormChange('address_district', e.target.value)}
                  />
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <Box sx={{ flex: '2 1 300px', minWidth: { xs: '100%', sm: 'calc(50% - 8px)' } }}>
                  <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                    {t('businessManagement.city')}
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder={t('businessManagement.city')}
                    value={formData.address_city}
                    onChange={(e) => handleFormChange('address_city', e.target.value)}
                  />
                </Box>
                <Box sx={{ flex: '1 1 150px', minWidth: { xs: '100%', sm: 'calc(25% - 8px)' } }}>
                  <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                    {t('businessManagement.state')}
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder={t('businessManagement.state')}
                    value={formData.address_state}
                    onChange={(e) => handleFormChange('address_state', e.target.value)}
                  />
                </Box>
                <Box sx={{ flex: '1 1 150px', minWidth: { xs: '100%', sm: 'calc(25% - 8px)' } }}>
                  <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                    {t('businessManagement.postalCode')}
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder={t('businessManagement.postalCode')}
                    value={formData.address_postal_code}
                    onChange={(e) => handleFormChange('address_postal_code', e.target.value)}
                  />
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Box
          sx={{
            mt: 4,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 2,
          }}
        >
          <Button
            variant="outlined"
            size="large"
            onClick={handleCancel}
            disabled={loading}
            sx={{
              textTransform: 'none',
              px: 4,
            }}
          >
            {t('businessManagement.cancel')}
          </Button>
          <Button
            variant="contained"
            size="large"
            onClick={handleSave}
            disabled={loading || (isEditMode && !hasChanges)}
            startIcon={loading && <CircularProgress size={20} color="inherit" />}
            sx={{
              textTransform: 'none',
              px: 4,
              fontWeight: 600,
            }}
          >
            {t('businessManagement.save')}
          </Button>
        </Box>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default BusinessForm;
